const hasUsableMessage = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return false;
  }

  if (trimmed.includes('[object Object]')) {
    return false;
  }

  if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
    return false;
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return false;
  }

  return true;
};

const firstValidationErrorMessage = (validationErrors) => {
  if (!validationErrors || typeof validationErrors !== 'object') {
    return null;
  }

  const baseErrors = Array.isArray(validationErrors.base)
    ? validationErrors.base.filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
    : [];
  if (baseErrors.length > 0) {
    return baseErrors[0];
  }

  const firstFieldError = Object.entries(validationErrors).find(([key, value]) => {
    if (key === 'base' || !Array.isArray(value)) {
      return false;
    }

    return value.some((entry) => typeof entry === 'string' && entry.trim().length > 0);
  });

  if (!firstFieldError) {
    return null;
  }

  const [, messages] = firstFieldError;
  return messages.find((entry) => typeof entry === 'string' && entry.trim().length > 0) || null;
};

const parseXHRError = (response) => {
  let status = response?.status;
  let extendedStatus = null;
  let title = "Fehler";
  let message = null;
  let validationErrors = null;
  let isValidationError = false;
  let validationMessage = null;

  if (typeof response === 'string') {
    message = response;
    return { status: null, extendedStatus: null, title, message };
  }

  if (!response || typeof response !== 'object') {
    return { status: null, extendedStatus: null, title, message: "Ein unbekannter Fehler ist aufgetreten." };
  }

  const jsonResponse = (() => {
    if (response.responseJSON && typeof response.responseJSON === 'object') {
      return response.responseJSON;
    }

    try {
      return JSON.parse(response.responseText);
    } catch (e) {
      return null;
    }
  })();

  try {
    if (hasUsableMessage(response.responseText)) {
      message = response.responseText;
    }

    if (!jsonResponse) {
      throw new Error('no-json');
    }

    if (jsonResponse.status_code) {
      status = jsonResponse.status_code;
      extendedStatus = jsonResponse.status_code;
    }

    if (jsonResponse.code || jsonResponse.status) {
      extendedStatus = jsonResponse.code || jsonResponse.status;
    }

    if (status == 400 && jsonResponse.errors) {
      validationErrors = jsonResponse.errors;
      isValidationError = true;

      validationMessage = Object.keys(jsonResponse.errors).map((key) => {
        if (key == 'base') {
          return jsonResponse.errors[key].join(', ');
        }

        return `"${key}": ${jsonResponse.errors[key].join(', ')}`;
      }).join(', ');
    } else if (jsonResponse.code || jsonResponse.status) {
      message = hasUsableMessage(jsonResponse.message) ? jsonResponse.message : message;
    } else if (jsonResponse.message) {
      message = hasUsableMessage(jsonResponse.message) ? jsonResponse.message : message;
    }
  } catch (e) {
    // Ignore JSON parsing errors
  }

  switch (status) {
    case 400:
      if (extendedStatus === 'invalid_resource' || isValidationError) {
        title = "400 - Ungültige Formulardaten";
        message = message || firstValidationErrorMessage(validationErrors) || "Ungültige Formulardaten";
      } else {
        title = "400 - Ungültige Anfrage";
        message = message || "Die Anfrage ist ungültig.";
      }
      break;
    case 404:
      title = "404 - Nicht gefunden";
      message = "Die angeforderte Resource wurde nicht gefunden.";
      break;
    case 401:
      title = "401 - Unauthorized";
      message = "Deine Sitzung ist abgelaufen oder Du bist nicht mehr eingeloggt. Bitte logge Dich ein!";
      break;
    case 402:
      title = "402 - Webhosting-Paket erforderlich";
      message = "Für diese Funktion ist ein Webhosting-Paket notwendig, in Deinem jetzigen Vertrag ist diese Funktion nicht enthalten. Bitte starte einen 14-tägigen kostenlosen Test oder buche ein Webhosting-Paket.";
      break;
    case 'shared_hosting_required':
      title = "Webhosting-Paket nötig";
      message = 'Auf Deinem Account ist derzeit kein Webhosting-Paket vorhanden. Bitte füge ein Webhosting-Paket unter "Webhosting-Paket" hinzu.';
      break;
    case 500:
      title = `Serverfehler ${status}`;
      message = "Es gab ein Serverproblem. Bitte versuche es später erneut.";
      break;
    case 422:
      title = "422 - Ungültige Anfrage";
      message = message || "Die Anfrage konnte nicht verarbeitet werden.";
      break;
    case 'email_unconfirmed':
      title = "Account-Aktivierung erforderlich";
      message = "Für diese Funktion ist die Aktivierung Deines Accounts notwendig. Bitte klicke den Link in der Bestätigungs-E-Mail an, die wir Dir geschickt haben. Keine E-Mail bekommen? Fordere eine Neue an.";
      break;
    default:
      if (extendedStatus) {
        title = `Fehler ${status}: ${extendedStatus}`;
        message = message || `Der Server hat den Fehler-Code ${status} (${extendedStatus}) zurückgegeben.`;
      } else if (status && response?.statusText) {
        title = `Fehler ${status}: ${response.statusText}`;
        message = message || `Der Server hat den Fehler-Code ${status} (${response.statusText}) zurückgegeben.`;
      }
      break;
  }

  if (!message) {
    if (status && response?.statusText) {
      message = `Der Server hat den Fehler-Code ${status} (${response.statusText}) zurückgegeben.`;
    } else {
      message = "Ein unbekannter Fehler ist aufgetreten.";
    }
  }

  return { status, extendedStatus, title, message, validationErrors, validationMessage, isValidationError };
};

export { parseXHRError };
